import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Input,
  Button,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Card,
  Alert,
  CircularProgress
} from '@mui/joy';
import { MagnifyingGlass as Search } from '@phosphor-icons/react';
import type { GoogleMapsPlace } from '../../types';

interface AddressSearchProps {
  onAddressSelect: (place: GoogleMapsPlace) => void;
  initialValue?: string;
}

export const AddressSearch: React.FC<AddressSearchProps> = ({
  onAddressSelect,
  initialValue = ''
}) => {
  const [searchValue, setSearchValue] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<GoogleMapsPlace[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const autocompleteRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesRef = useRef<google.maps.places.PlacesService | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Initialize Google Maps services when available
    const initializeGoogleMaps = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        try {
          autocompleteRef.current = new window.google.maps.places.AutocompleteService();
          
          // Create a hidden map for PlacesService
          if (mapRef.current) {
            const map = new window.google.maps.Map(mapRef.current, {
              center: { lat: 0, lng: 0 },
              zoom: 1
            });
            placesRef.current = new window.google.maps.places.PlacesService(map);
          }
          console.log('Google Maps services initialized successfully');
        } catch (error) {
          console.error('Error initializing Google Maps services:', error);
          setError('Failed to initialize Google Maps services');
        }
      } else {
        // Wait for Google Maps to load
        setTimeout(initializeGoogleMaps, 100);
      }
    };

    initializeGoogleMaps();
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleSearch = async (query: string) => {
    if (!query.trim() || query.trim().length < 3 || !autocompleteRef.current) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const request = {
        input: query,
        types: ['address'],
        componentRestrictions: { country: 'us' }
      };

      autocompleteRef.current.getPlacePredictions(request, (predictions, status) => {
        setIsLoading(false);
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          // Convert predictions to our format
          const places: GoogleMapsPlace[] = predictions.map(prediction => ({
            place_id: prediction.place_id,
            formatted_address: prediction.description,
            name: prediction.structured_formatting.main_text,
            geometry: {
              location: { lat: 0, lng: 0 } // Will be filled when selected
            }
          }));
          
          setSuggestions(places);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
          if (status !== window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            setError('Error searching for addresses');
          }
        }
      });
    } catch (err) {
      setIsLoading(false);
      setError('Error searching for addresses');
      console.error('Address search error:', err);
    }
  };

  const handleSuggestionClick = (place: GoogleMapsPlace) => {
    if (!placesRef.current) {
      setError('Google Maps service not available');
      return;
    }

    // Get detailed place information including coordinates
    const request = {
      placeId: place.place_id,
      fields: ['formatted_address', 'geometry', 'name']
    };

    placesRef.current.getDetails(request, (result, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && result) {
        const detailedPlace: GoogleMapsPlace = {
          place_id: place.place_id,
          formatted_address: result.formatted_address || place.formatted_address,
          name: result.name || place.name,
          geometry: {
            location: {
              lat: result.geometry?.location?.lat() || 0,
              lng: result.geometry?.location?.lng() || 0
            }
          }
        };
        
        setSearchValue(detailedPlace.formatted_address);
        setShowSuggestions(false);
        onAddressSelect(detailedPlace);
      } else {
        setError('Error getting place details');
      }
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Clear suggestions immediately if input is too short
    if (value.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    // Debounce search - wait for user to stop typing
    debounceTimeoutRef.current = setTimeout(() => {
      if (value.trim().length >= 3) {
        handleSearch(value);
      }
    }, 800); // Wait 800ms after user stops typing
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Hidden map div for PlacesService */}
      <div ref={mapRef} style={{ display: 'none' }} />
      
      <Typography level="body-sm" sx={{ mb: 1, fontWeight: 'bold' }}>
        Property Address
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Input
          placeholder="Enter property address..."
          value={searchValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          startDecorator={<Search size={18} />}
          sx={{ flexGrow: 1 }}
          disabled={isLoading}
        />
        <Button 
          variant="solid" 
          color="primary"
          onClick={() => handleSearch(searchValue)}
          disabled={isLoading || !searchValue.trim()}
        >
          {isLoading ? <CircularProgress size="sm" /> : 'Search'}
        </Button>
      </Box>

      {error && (
        <Alert color="danger" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <Card
          variant="outlined"
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            mt: 1,
            maxHeight: 300,
            overflow: 'auto',
            // The global glass Card style is ~50% transparent, which lets the
            // form text underneath bleed through this floating overlay (nested
            // backdrop-filter cannot blur a parent glass card's content), so
            // this dropdown needs a near-opaque surface to stay readable.
            backgroundColor: 'rgba(255, 255, 255, 0.96) !important',
            '[data-joy-color-scheme="dark"] &': {
              backgroundColor: 'rgba(26, 24, 42, 0.96) !important',
            },
          }}
        >
          <List size="sm">
            {suggestions.map((suggestion) => (
              <ListItem key={suggestion.place_id} sx={{ p: 0 }}>
                <ListItemButton
                  onClick={() => handleSuggestionClick(suggestion)}
                  sx={{ width: '100%' }}
                >
                  <Box>
                    <Typography level="body-sm">
                      {suggestion.name}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      {suggestion.formatted_address}
                    </Typography>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Card>
      )}
    </Box>
  );
};